import classNames from "classnames";
import React from "react";

import { WSAvatar, WSPill } from "../common";
import { WSButton } from "../core/WSButton/WSButton.component";
import { WSIcon } from "../core/WSIcon/WSIcon.component";
import { WSText } from "../core/WSText/WSText.component";
import { WSElement } from "../WSElement/WSElement.component";

import { WSSelectOption } from "./types";
import styles from "./WSSelect.module.scss";

type Props = {
  onClick?: () => void;
  isActive?: boolean;
  mode?: "single" | "multiple";
  isSelected?: boolean;
  testIdBase?: string;
  noTranslate?: boolean;
} & WSSelectOption;

const Checkbox: React.FC<{
  checked?: boolean;
  indeterminate?: boolean;
  testId?: string;
}> = ({ checked, indeterminate, testId }) => {
  const state = indeterminate
    ? "indeterminate"
    : checked
    ? "checked"
    : "unchecked";
  return (
    <span
      className={classNames(
        styles.checkbox,
        checked && styles.checkboxChecked,
        indeterminate && styles.checkboxIndeterminate
      )}
      data-testid={testId ?? `checkbox-${state}`}
      data-state={state}
    >
      {checked && <WSIcon name="check" size="XS" color="white" />}
      {indeterminate && <span className={styles.checkboxDash} />}
    </span>
  );
};

export { Checkbox };

export const Option: React.FC<Props> = ({
  onClick,
  label,
  value,
  isActive,
  icon,
  avatar,
  labels,
  pill,
  action,
  mode,
  isSelected,
  testIdBase,
  noTranslate
}) => {
  const isMultiple = mode === "multiple";
  const optionTestId = testIdBase
    ? `${testIdBase}Option-${value}`
    : `selectOption-${value}`;

  return (
    <WSElement
      onClick={onClick}
      className={classNames(
        styles.option,
        isActive && !isMultiple && styles.active,
        isMultiple && isSelected && styles.selected,
        isMultiple && isActive && styles.active
      )}
      data-testid={optionTestId}
      data-option-value={value}
      data-active={isActive || undefined}
      data-selected={isSelected || undefined}
      translate={noTranslate ? "no" : undefined}
    >
      {isMultiple && (
        <Checkbox checked={isSelected} testId={`${optionTestId}-checkbox`} />
      )}

      <WSElement className={styles.optionContent}>
        {icon ? (
          <WSIcon name={icon} size="S" color="gray600" />
        ) : avatar ? (
          <WSAvatar
            color="white"
            colorBackground="gray500"
            size="S"
            {...avatar}
          />
        ) : null}

        <WSElement className={styles.optionLabel}>
          <WSText.ParagraphSm color="gray600">{label}</WSText.ParagraphSm>
          {labels && (
            <WSText.ParagraphXs color="gray400">
              {labels?.join(" • ")}
            </WSText.ParagraphXs>
          )}
        </WSElement>

        {pill ? (
          <WSPill {...pill} />
        ) : action ? (
          <WSButton.Link onClick={action.onClick} size="S" type="button">
            {action.text}
          </WSButton.Link>
        ) : null}
      </WSElement>

      {!isMultiple && isActive && (
        <WSIcon name="check" size="S" color="blue400" />
      )}
    </WSElement>
  );
};
